import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { primeCaptainAudioOnGesture } from '../../utils/captainAlerts';
import { getFixedChromePortalTarget } from '../../utils/fixedChromePortal';
import {
  CAPTAIN_ASSISTANCE_OPEN_EVENT,
  CAPTAIN_PANEL_MINIMIZE_EVENT,
} from '../../utils/captainPanelEvents';
import {
  isPopoverDismissedForMessage,
  markPopoverDismissed,
  isAutoOpenUpdatesEnabled,
  setAutoOpenUpdatesEnabled,
} from '../../utils/captainSessionMemory';
import { useVirtualCaptain } from '../context/VirtualCaptainContext';
import { CaptainAvatar } from './CaptainParts';
import CaptainPopover from './CaptainPopover';
import CaptainPanel from './CaptainPanel';
import type { CaptainMessageType } from '../types/events';

const AUTO_OPEN_MESSAGE_TYPES: CaptainMessageType[] = [
  'welcome',
  'nudge',
  'placed',
  'confirmed',
  'preparing',
  'ready',
  'complete',
  'cancelled',
  'item_status',
];

const CaptainWidget: React.FC = () => {
  const {
    active,
    restaurantName,
    messages,
    latestMessage,
    ongoingOrder,
    voiceEnabled,
    setVoiceEnabled,
    unlockVoice,
    showVoiceTip,
    setShowVoiceTip,
    dismissVoiceTip,
    enableVoiceFromTip,
    assistanceAvailable,
    chatEnabled,
    chatMessages,
    streamingText,
    chatConnected,
    chatConnectionFailed,
    sendChatMessage,
    submitChoiceSelection,
    appName,
  } = useVirtualCaptain();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [focusChat, setFocusChat] = useState(false);
  const [focusAssistance, setFocusAssistance] = useState(false);
  const [autoOpenUpdates, setAutoOpenUpdates] = useState(isAutoOpenUpdatesEnabled);
  const seenMessageIdRef = useRef<string | null>(null);
  const openedOnceRef = useRef(false);
  const autoOpenUpdatesRef = useRef(autoOpenUpdates);
  autoOpenUpdatesRef.current = autoOpenUpdates;

  const shouldSuppressPopover = (messageId: string | undefined): boolean =>
    Boolean(appName && messageId && isPopoverDismissedForMessage(appName, messageId));

  const markPopoverDismissedForCurrent = () => {
    if (appName && latestMessage?.id) {
      markPopoverDismissed(appName, latestMessage.id);
    }
  };

  const tryOpenPopoverForMessage = (messageId: string | undefined) => {
    if (shouldSuppressPopover(messageId) || !autoOpenUpdatesRef.current) {
      setHasUnread(true);
      return;
    }
    setPopoverOpen(true);
    setHasUnread(false);
  };

  const handleToggleAutoOpenUpdates = () => {
    const next = !autoOpenUpdates;
    setAutoOpenUpdates(next);
    setAutoOpenUpdatesEnabled(next);
  };

  useEffect(() => {
    const captainOpen = popoverOpen || expanded;
    if (captainOpen) {
      document.body.classList.add('peppar-captain-open');
    } else {
      document.body.classList.remove('peppar-captain-open');
    }
    return () => {
      document.body.classList.remove('peppar-captain-open');
    };
  }, [popoverOpen, expanded]);

  useEffect(() => {
    if (active && latestMessage && !openedOnceRef.current) {
      openedOnceRef.current = true;
      tryOpenPopoverForMessage(latestMessage.id);
    }
  }, [active, latestMessage, appName]);

  useEffect(() => {
    const minimize = () => {
      setExpanded(false);
      setPopoverOpen(false);
    };
    const openAssistance = () => {
      if (voiceEnabled) {
        unlockVoice();
        primeCaptainAudioOnGesture();
      }
      setFocusChat(false);
      setFocusAssistance(false);
      setExpanded(true);
      setPopoverOpen(false);
      setHasUnread(false);
      window.requestAnimationFrame(() => setFocusAssistance(true));
    };
    window.addEventListener(CAPTAIN_PANEL_MINIMIZE_EVENT, minimize);
    window.addEventListener(CAPTAIN_ASSISTANCE_OPEN_EVENT, openAssistance);
    return () => {
      window.removeEventListener(CAPTAIN_PANEL_MINIMIZE_EVENT, minimize);
      window.removeEventListener(CAPTAIN_ASSISTANCE_OPEN_EVENT, openAssistance);
    };
  }, [unlockVoice, voiceEnabled]);

  // New messages: auto-open popover for important updates; pulse launcher otherwise.
  useEffect(() => {
    if (!latestMessage) return;

    if (seenMessageIdRef.current === null) {
      seenMessageIdRef.current = latestMessage.id;
      tryOpenPopoverForMessage(latestMessage.id);
      if (!autoOpenUpdatesRef.current) {
        setPulse(true);
        const pulseTimer = setTimeout(() => setPulse(false), 3000);
        return () => clearTimeout(pulseTimer);
      }
      return;
    }
    if (latestMessage.id === seenMessageIdRef.current) return;
    seenMessageIdRef.current = latestMessage.id;

    if (latestMessage.type === 'wait_time' || AUTO_OPEN_MESSAGE_TYPES.includes(latestMessage.type)) {
      tryOpenPopoverForMessage(latestMessage.id);
      setPulse(true);
      const pulseTimer = setTimeout(() => setPulse(false), 3000);
      return () => clearTimeout(pulseTimer);
    }

    if (expanded || popoverOpen) return;

    setHasUnread(true);
    setPulse(true);
    const pulseTimer = setTimeout(() => setPulse(false), 3000);
    return () => clearTimeout(pulseTimer);
  }, [latestMessage?.id, latestMessage?.type, expanded, popoverOpen, appName]);

  if (!active) {
    return null;
  }

  const handleLauncherClick = () => {
    if (voiceEnabled) {
      unlockVoice();
      primeCaptainAudioOnGesture();
    }
    if (expanded) {
      setExpanded(false);
      setPopoverOpen(true);
      return;
    }
    if (!latestMessage) {
      setExpanded(true);
      setPopoverOpen(false);
      setHasUnread(false);
      return;
    }
    setPopoverOpen((open) => !open);
    setHasUnread(false);
  };

  const handleStartChat = () => {
    setFocusAssistance(false);
    setFocusChat(true);
    setExpanded(true);
    setPopoverOpen(false);
    setHasUnread(false);
  };

  const handleClosePanel = () => {
    markPopoverDismissedForCurrent();
    setExpanded(false);
    setPopoverOpen(false);
    setFocusChat(false);
    setFocusAssistance(false);
  };

  const handleDismissPopover = () => {
    markPopoverDismissedForCurrent();
    setPopoverOpen(false);
  };

  const handleExpandPanel = () => {
    setFocusChat(false);
    setFocusAssistance(false);
    setExpanded(true);
    setPopoverOpen(false);
  };

  const ui = (
    <>
      {expanded && (
        <button
          type="button"
          className="vc-backdrop"
          aria-label="Close Peppy AI panel"
          onClick={handleClosePanel}
        />
      )}

      <div className="vc-widget" aria-live="polite">
        <button
          type="button"
          className="vc-widget__launcher"
          onClick={handleLauncherClick}
          aria-label="Peppy AI"
        >
          <CaptainAvatar size={56} ring pulse={pulse} />
          {!popoverOpen && !expanded && hasUnread && (
            <span className="vc-widget__badge" aria-hidden />
          )}
        </button>

        {popoverOpen && !expanded && latestMessage && (
          <CaptainPopover
            message={latestMessage}
            restaurantName={restaurantName}
            voiceOn={voiceEnabled}
            onToggleVoice={() => setVoiceEnabled(!voiceEnabled)}
            autoOpenUpdates={autoOpenUpdates}
            onToggleAutoOpenUpdates={handleToggleAutoOpenUpdates}
            onExpand={handleExpandPanel}
            onStartChat={chatEnabled ? handleStartChat : undefined}
            chatEnabled={chatEnabled}
            onDismiss={handleDismissPopover}
            showVoiceTip={showVoiceTip && !voiceEnabled}
            onEnableVoice={() => {
              if (enableVoiceFromTip) {
                enableVoiceFromTip();
              } else {
                setVoiceEnabled(true);
                setShowVoiceTip(false);
              }
            }}
            onDismissVoiceTip={() => {
              if (dismissVoiceTip) {
                dismissVoiceTip();
              } else {
                setShowVoiceTip(false);
              }
            }}
            assistanceAvailable={assistanceAvailable}
            order={ongoingOrder}
          />
        )}
      </div>

      {expanded && (
        <CaptainPanel
          messages={messages}
          restaurantName={restaurantName}
          order={ongoingOrder}
          voiceOn={voiceEnabled}
          onToggleVoice={() => setVoiceEnabled(!voiceEnabled)}
          autoOpenUpdates={autoOpenUpdates}
          onToggleAutoOpenUpdates={handleToggleAutoOpenUpdates}
          onClose={handleClosePanel}
          assistanceAvailable={assistanceAvailable}
          chatEnabled={chatEnabled}
          chatMessages={chatMessages}
          streamingText={streamingText}
          chatConnected={chatConnected}
          chatConnectionFailed={chatConnectionFailed}
          onSendChat={sendChatMessage}
          onChoiceSelect={submitChoiceSelection}
          focusChat={focusChat}
          focusAssistance={focusAssistance}
        />
      )}
    </>
  );

  if (typeof document === 'undefined') {
    return ui;
  }

  return createPortal(ui, getFixedChromePortalTarget());
};

export default CaptainWidget;
